import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GetStartedButton } from '../../components/common/GetStartedButton';
import { TeamLogoBox } from '../../components/common/TeamLogoBox';
import { TeamLogo } from '../../components/sections/LeaderboardSection';
import { SegmentedToggle } from '../../components/common/SegmentedToggle';

// Public previews of player and team profiles, opened from the homepage
// leaderboard. Each shows only what the clicked leaderboard row already shows
// (player: name, photo, position, school, the ranked stat; team: name and
// logo), passed along with the click. The server is never asked for anything
// else (no rosters), and the rest of the layout is placeholder text shown
// blurred, so nothing can be read by un-blurring it. Opened without that click
// (e.g. a shared link), a preview asks the visitor to sign up.

const CARD = 'bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-5 sm:p-6';

// Placeholder shown in place of member-only values. Screen readers hear a
// short note instead of the fake text.
const Blurred = ({ children, className = '' }) => (
  <>
    <span aria-hidden="true" className={`blur-[5px] select-none pointer-events-none ${className}`}>
      {children}
    </span>
    <span className="sr-only">Members only</span>
  </>
);

const LockIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const SectionCard = ({ title, children, className = '' }) => (
  <section className={`${CARD} ${className}`}>
    <h2 className="flex items-center justify-between gap-3 text-lg font-extrabold text-jp-ink mb-4">
      {title}
      {/* Blue lock in light mode, yellow on navy in dark mode */}
      <span className="w-7 h-7 flex-shrink-0 rounded-full bg-jp-accent/15 text-jp-accent dark:bg-jp-navy dark:text-jp-gold flex items-center justify-center" title="Members only">
        <LockIcon className="w-3.5 h-3.5" />
      </span>
    </h2>
    {children}
  </section>
);

// Bulleted list of members-only placeholder entries.
const BlurredList = ({ items }) => (
  <ul className="space-y-2 text-jp-muted text-sm">
    {items.map((item) => (
      <li key={item} className="flex gap-2">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-jp-accent flex-shrink-0" aria-hidden="true"></span>
        <Blurred>{item}</Blurred>
      </li>
    ))}
  </ul>
);

const InfoGrid = ({ items, wide = false }) => (
  <dl className={`grid grid-cols-2 sm:grid-cols-3 ${wide ? 'lg:grid-cols-4' : ''} gap-x-4 gap-y-4`}>
    {items.map(({ label, value, visible }) => (
      <div key={label} className="min-w-0">
        <dt className="text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1">{label}</dt>
        <dd className="text-jp-ink text-sm font-semibold truncate">
          {visible ? value : <Blurred>{value}</Blurred>}
        </dd>
      </div>
    ))}
  </dl>
);

const ICON_CLASS = 'w-4 h-4 text-cyan-400 flex-shrink-0';

// Phone, email, and the player's own Instagram and X accounts (instaURL / xURL
// on the real profile). All are members-only, so every value is a placeholder.
const CONTACT_ITEMS = [
  {
    label: 'Phone',
    placeholder: '(555) 555-0123',
    icon: (
      <svg className={ICON_CLASS} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.3a1 1 0 01.95.68l1.2 3.6a1 1 0 01-.27 1.05L7.6 9.9a12 12 0 006.5 6.5l1.57-1.58a1 1 0 011.05-.27l3.6 1.2a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z" />
      </svg>
    ),
  },
  {
    label: 'Email',
    placeholder: 'player.name@email.com',
    icon: (
      <svg className={ICON_CLASS} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    label: 'Instagram',
    placeholder: '@player.handle',
    icon: (
      <svg className={ICON_CLASS} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    label: 'X',
    placeholder: '@playerhandle',
    icon: (
      <svg className={ICON_CLASS} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
      </svg>
    ),
  },
];

const ContactDetails = () => (
  <dl className="flex flex-wrap gap-x-6 gap-y-2 mt-4">
    {CONTACT_ITEMS.map((item) => (
      <div key={item.label} className="flex items-center gap-2 text-sm">
        {item.icon}
        <dt className="sr-only">{item.label}</dt>
        <dd className="text-white font-semibold">
          <Blurred>{item.placeholder}</Blurred>
        </dd>
      </div>
    ))}
  </dl>
);

// Player headshot (profileImage, synced from Presto), shown unblurred to
// everyone. Falls back to a silhouette until a photo is available.
const PlayerPhoto = ({ name, src }) => {
  const [failed, setFailed] = useState(false);
  const frame = 'w-32 h-32 sm:w-44 sm:h-44 flex-shrink-0 rounded-2xl border-2 border-cyan-400/60 overflow-hidden';

  if (src && !failed) {
    return <img src={src} alt={name} onError={() => setFailed(true)} className={`${frame} object-cover bg-white/10`} />;
  }

  return (
    <div className={`${frame} bg-white/10 flex items-end justify-center`} role="img" aria-label={`${name} photo coming soon`}>
      <svg className="w-4/5 h-4/5 text-white/40" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6z" />
      </svg>
    </div>
  );
};

// Baseball-Reference style season table (one row per season plus a career
// total) with the same filters as the member profile: stat type, split, and
// standard/advanced. Column sets match the member profile's; every value is a
// blurred placeholder.
const STAT_TYPES = ['Hitting', 'Pitching', 'Fielding', 'Base Running'];
const SPLITS = ['Overall Stats', 'Regular Season', 'Regular Season Conference', 'Summer Ball'];

// [abbr, full name, placeholder]
const STAT_COLUMNS = {
  Hitting: {
    Standard: [
      ['GP', 'Games Played', '00'],
      ['AB', 'At Bats', '000'],
      ['H', 'Hits', '00'],
      ['RBI', 'Runs Batted In', '00'],
      ['2B', 'Doubles', '00'],
      ['3B', 'Triples', '0'],
      ['HR', 'Home Runs', '00'],
      ['K', 'Strikeouts', '00'],
      ['BB', 'Walks', '00'],
      ['AVG', 'Batting Average', '.000'],
      ['OBP', 'On-Base Percentage', '.000'],
      ['SLG', 'Slugging Percentage', '.000'],
    ],
    Advanced: [
      ['ISO', 'Isolated Power', '.000'],
      ['wOBA', 'Weighted On-Base Average', '.000'],
      ['wRC+', 'Weighted Runs Created Plus', '000'],
      ['OPS+', 'Adjusted OPS', '000'],
      ['K%', 'Strikeout Rate', '00.0%'],
      ['BB%', 'Walk Rate', '00.0%'],
      ['K/BB', 'Strikeouts per Walk', '0.00'],
      ['BABIP', 'Batting Average on Balls in Play', '.000'],
    ],
  },
  Pitching: {
    Standard: [
      ['APP', 'Appearances', '00'],
      ['GS', 'Games Started', '00'],
      ['IP', 'Innings Pitched', '00.0'],
      ['ERA', 'Earned Run Average', '0.00'],
      ['W', 'Wins', '0'],
      ['L', 'Losses', '0'],
      ['K', 'Strikeouts', '000'],
      ['BB', 'Walks', '00'],
      ['H', 'Hits Allowed', '00'],
      ['R', 'Runs Allowed', '00'],
      ['ER', 'Earned Runs', '00'],
      ['HR', 'Home Runs Allowed', '0'],
      ['HBP', 'Hit By Pitch', '0'],
      ['CG', 'Complete Games', '0'],
      ['SHO', 'Shutouts', '0'],
    ],
    Advanced: [
      ['WHIP', 'Walks + Hits per Inning Pitched', '0.00'],
      ['FIP', 'Fielding Independent Pitching', '0.00'],
      ['K%', 'Strikeout Rate', '00.0%'],
      ['BB%', 'Walk Rate', '00.0%'],
      ['K-BB%', 'Strikeout Minus Walk Rate', '00.0%'],
      ['FIP+', 'Adjusted FIP', '000'],
      ['ERA+', 'Adjusted ERA', '000'],
      ['LOB%', 'Left On Base Rate', '00.0%'],
      ['BAA', 'Batting Average Against', '.000'],
    ],
  },
  Fielding: {
    Standard: [
      ['GP', 'Games Played', '00'],
      ['TC', 'Total Chances', '000'],
      ['PO', 'Putouts', '000'],
      ['A', 'Assists', '00'],
      ['E', 'Errors', '0'],
      ['FLD%', 'Fielding Percentage', '.000'],
      ['DP', 'Double Plays', '00'],
      ['SBA', 'Stolen Bases Allowed', '00'],
      ['RCS', 'Runners Caught Stealing', '00'],
      ['RCS%', 'Caught Stealing Rate', '00.0%'],
      ['PB', 'Passed Balls', '0'],
      ['CI', "Catcher's Interference", '0'],
    ],
  },
  'Base Running': {
    Standard: [
      ['GP', 'Games Played', '00'],
      ['R', 'Runs Scored', '00'],
      ['TB', 'Total Bases', '000'],
      ['SB', 'Stolen Bases', '00'],
      ['CS', 'Caught Stealing', '0'],
    ],
  },
};

const STAT_ROWS = ['Season 1', 'Season 2'];

// Pill-shaped dropdown matching the homepage leaderboard's "Sort by" picker.
const FilterSelect = ({ label, options, value, onChange }) => (
  <label className="relative flex items-center">
    <span className="sr-only">{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="appearance-none bg-none cursor-pointer rounded-full bg-jp-section border border-jp-outline/70 hover:border-jp-accent/60 pl-4 pr-9 py-2 text-xs font-bold text-jp-accent-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    <svg className="pointer-events-none absolute right-3 w-3.5 h-3.5 text-jp-accent-ink" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </label>
);

const StatsTable = ({ category, team, division }) => {
  const [statType, setStatType] = useState(category === 'pitching' ? 'Pitching' : 'Hitting');
  const [split, setSplit] = useState(SPLITS[0]);
  const [depth, setDepth] = useState('Standard');

  const depthOptions = Object.keys(STAT_COLUMNS[statType]);
  const activeDepth = depthOptions.includes(depth) ? depth : depthOptions[0];
  const columns = STAT_COLUMNS[statType][activeDepth];
  // Summer ball is played for a summer team in a summer league, not the JUCO team.
  const isSummer = split === 'Summer Ball';

  const cell = 'px-2.5 py-2 text-right tabular-nums whitespace-nowrap';
  const sticky = 'sticky left-0 z-10 text-left whitespace-nowrap px-3 py-2';

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <FilterSelect label="Stat type" options={STAT_TYPES} value={statType} onChange={setStatType} />
        <FilterSelect label="Games" options={SPLITS} value={split} onChange={setSplit} />
        <FilterSelect label="Stat detail" options={depthOptions} value={activeDepth} onChange={setDepth} />
      </div>
      <div className="overflow-x-auto rounded-xl border border-jp-line">
        <table className="w-full text-xs sm:text-sm border-collapse">
          <caption className="text-left text-jp-ink font-bold px-3 py-2 bg-jp-section border-b border-jp-line">
            {activeDepth} {statType} <span className="text-jp-muted font-semibold">· {split}</span>
          </caption>
          <thead>
            <tr className="bg-jp-section text-jp-muted border-b-2 border-jp-ink/20">
              <th scope="col" className={`${sticky} bg-jp-section font-bold`}>Year</th>
              <th scope="col" className="px-2.5 py-2 text-left font-bold whitespace-nowrap">Team</th>
              <th scope="col" className="px-2.5 py-2 text-left font-bold whitespace-nowrap">{isSummer ? 'League' : 'Division'}</th>
              {columns.map(([abbr, name]) => (
                <th key={abbr} scope="col" className={`${cell} font-bold`}>
                  <abbr title={name} className="no-underline cursor-help">{abbr}</abbr>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-jp-ink">
            {STAT_ROWS.map((season, index) => (
              <tr key={season} className={`border-b border-jp-line ${index % 2 ? 'bg-jp-section/60' : 'bg-jp-card'}`}>
                <th scope="row" className={`${sticky} font-semibold ${index % 2 ? 'bg-jp-section' : 'bg-jp-card'}`}>
                  <Blurred>2025</Blurred>
                </th>
                {isSummer ? (
                  <>
                    <td className="px-2.5 py-2 whitespace-nowrap"><Blurred>Summer Team</Blurred></td>
                    <td className="px-2.5 py-2 whitespace-nowrap"><Blurred>Summer League</Blurred></td>
                  </>
                ) : (
                  <>
                    <td className="px-2.5 py-2 whitespace-nowrap text-jp-accent-ink font-semibold">{team}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{division || <Blurred>NJCAA D1</Blurred>}</td>
                  </>
                )}
                {columns.map(([abbr, , placeholder]) => (
                  <td key={abbr} className={cell}>
                    <Blurred>{placeholder}</Blurred>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-jp-card font-bold text-jp-ink border-t-2 border-jp-ink/20">
              <th scope="row" className={`${sticky} bg-jp-card`}>Career</th>
              <td className="px-2.5 py-2"></td>
              <td className="px-2.5 py-2"></td>
              {columns.map(([abbr, , placeholder]) => (
                <td key={abbr} className={cell}>
                  <Blurred>{placeholder}</Blurred>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

const PLACEHOLDER_PARAGRAPH =
  'Placeholder profile text for members. Coaches see the full write-up here, including background, strengths, and what makes this player a fit for the next level.';

// Navy header with the public basics on the left and an optional item (the
// team logo) on the right, followed by the unlock bar.
const PreviewHeader = ({ eyebrow, title, subtitle, details, logo, aside, footer, redirectTo }) => (
  <>
    <section className="bg-jp-band px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-10">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="flex items-center gap-5 min-w-0">
          {logo}
          <div className="min-w-0">
            <p className="text-yellow-300 text-sm font-bold uppercase tracking-widest mb-2">{eyebrow}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-anton leading-[1.1] tracking-tight text-white uppercase">
              {title}
            </h1>
            {subtitle && <div className="text-slate-300 text-sm sm:text-base mt-2">{subtitle}</div>}
            {details}
          </div>
        </div>
        {aside}
      </div>
      {footer && <div className="max-w-7xl mx-auto">{footer}</div>}
    </section>
    <UnlockBar redirectTo={redirectTo} />
  </>
);

// Full-width strip under the header inviting visitors to unlock the data.
const UnlockBar = ({ redirectTo }) => (
  <div className="bg-jp-section border-b border-jp-line px-4 sm:px-6 lg:px-8 py-4">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 flex-shrink-0 rounded-full bg-jp-accent/15 text-jp-accent dark:bg-jp-navy dark:text-jp-gold flex items-center justify-center">
          <LockIcon className="w-5 h-5" />
        </span>
        <div>
          <p className="text-jp-ink font-extrabold">Unlock the full profile</p>
          <p className="text-jp-muted text-sm">Members see every stat, video, and detail on this page.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Link
          to="/auth/login"
          state={{ redirectTo }}
          className="text-sm font-semibold text-jp-accent-ink underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent rounded"
        >
          Already a member? Log in
        </Link>
        <GetStartedButton>SIGN UP</GetStartedButton>
      </div>
    </div>
  </div>
);

// Shown when the page is opened without a leaderboard click to build it from.
const MembersOnly = ({ kind }) => (
  <main className="min-h-screen bg-jp-page text-jp-ink text-left px-4 sm:px-6 lg:px-8 pt-36 pb-20">
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-extrabold mb-3">{kind === 'team' ? 'Team' : 'Player'} profiles are for members.</h1>
      <p className="text-jp-muted mb-6">Sign up or log in to see this {kind}'s full profile.</p>
      <div className="flex flex-wrap items-center gap-4">
        <GetStartedButton>SIGN UP</GetStartedButton>
        <Link to="/auth/login" className="font-semibold text-jp-accent-ink hover:underline">
          Already a member? Log in
        </Link>
      </div>
    </div>
  </main>
);

// "#1 in AVG · NJCAA D1 · .556 · 2025–26", from the leaderboard row that was
// clicked. The rank is national unless the leaderboard was filtered to a division.
const LeaderboardStat = ({ rank, label, value, season, division }) => (
  <p className="mt-3 inline-flex flex-wrap items-center gap-x-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs sm:text-sm">
    <span className="font-bold text-yellow-300">#{rank} in {label} · {division || 'National'}</span>
    <span className="text-white font-semibold tabular-nums">{value}</span>
    {season && <span className="text-slate-300">· {season} season</span>}
  </p>
);

export const PlayerPreviewPage = () => {
  const { state } = useLocation();
  const player = state?.player;

  if (!player) return <MembersOnly kind="player" />;

  // The school's preview, built from the same leaderboard row
  const teamLink = player.teamId
    ? {
        to: `/preview/team/${player.teamId}`,
        state: { team: { id: player.teamId, name: player.team, logo: player.teamLogo }, division: state.division },
      }
    : null;

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      <PreviewHeader
        eyebrow="Player Profile"
        title={player.name}
        redirectTo={{ kind: 'player', id: player.id }}
        subtitle={
          <span>
            {player.position && <>{player.position} · </>}
            <TeamPreviewLink {...teamLink} className="text-white font-semibold hover:text-cyan-400 hover:underline">
              {player.team}
            </TeamPreviewLink>
            {player.jerseyNumber && <span className="ml-2 text-white font-bold">#{player.jerseyNumber}</span>}
          </span>
        }
        aside={
          // Team logo on the right, as on the full profile
          <TeamPreviewLink
            {...teamLink}
            label={`View ${player.team} team profile`}
            className="self-start sm:self-auto rounded-2xl"
          >
            <TeamLogoBox name={player.team} logo={player.teamLogo} badge={Boolean(teamLink?.to)} />
          </TeamPreviewLink>
        }
        logo={<PlayerPhoto name={player.name} src={player.profileImage} />}
        details={
          <>
            {state.stat && <LeaderboardStat {...state.stat} division={state.division} />}
            <ContactDetails />
          </>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Player Information" className="lg:col-span-2">
          <InfoGrid
            items={[
              { label: 'Position', value: player.position || '-', visible: true },
              { label: 'College', value: player.team, visible: true },
              { label: 'Class', value: 'SO' },
              { label: 'B/T', value: 'R/R' },
              { label: 'Height', value: '6\'0"' },
              { label: 'Weight', value: '185 lbs' },
              { label: 'Date of Birth', value: '01/01/2005' },
              { label: 'Hometown', value: 'Hometown, ST' },
              { label: 'High School', value: 'High School Name' },
              { label: 'Commitment', value: 'Commitment status' },
              { label: 'NCAA ID Number', value: '0000000000' },
            ]}
          />
        </SectionCard>

        <SectionCard title="Season Statistics" className="lg:col-span-2">
          <StatsTable category={state.category} team={player.team} division={state.division} />
        </SectionCard>

        <SectionCard title="Video" className="lg:col-span-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {['Highlight 1', 'Highlight 2', 'Highlight 3'].map((clip) => (
              <div key={clip} className="relative aspect-video rounded-xl overflow-hidden bg-[#0B1220]">
                <div className="absolute inset-0 blur-md bg-gradient-to-br from-jp-navy via-[#17243A] to-cyan-900" aria-hidden="true"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-12 h-12 rounded-full bg-white/15 border border-white/30 flex items-center justify-center text-cyan-400 dark:text-yellow-300">
                    <LockIcon className="w-5 h-5" />
                  </span>
                </div>
                <span className="sr-only">{clip}: members only</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Bio" className="lg:col-span-2">
          <p className="text-jp-muted text-sm leading-relaxed">
            <Blurred>{PLACEHOLDER_PARAGRAPH}</Blurred>
          </p>
        </SectionCard>

        <SectionCard title="Previous Teams">
          <BlurredList items={['Previous College', 'Summer League Team', 'High School Team']} />
        </SectionCard>

        <SectionCard title="Awards & Achievements">
          <BlurredList items={['All-Conference First Team', 'Player of the Week', 'Academic Honor Roll']} />
        </SectionCard>

        <SectionCard title="Academic Information" className="lg:col-span-2">
          <InfoGrid
            items={[
              { label: 'GPA', value: '3.00' },
              { label: 'NCAA Eligibility', value: 'Eligibility status' },
              { label: 'Transcripts', value: 'On file' },
            ]}
          />
        </SectionCard>
      </div>
    </main>
  );
};

// Links to the school's preview when it has one, otherwise plain text.
const TeamPreviewLink = ({ to, state, label, className = '', children }) =>
  to ? (
    <Link
      to={to}
      state={state}
      aria-label={label}
      className={`${className} rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400`}
    >
      {children}
    </Link>
  ) : (
    <span className={className.replace(/hover:\S+/g, '')}>{children}</span>
  );

// Laid out like a coach's team page (pages/coach/team/CoachTeamProfilePage.jsx):
// navy header with the head coach's contact row, Team Information, then
// Roster / Schedule / Statistics tabs. Real rosters, schedules and stats are
// members only and never sent here, so every value below is a placeholder.

// No., Name, Position, Class, Height, Weight, B/T, as on the coach roster table
const ROSTER_PLACEHOLDERS = [
  ['00', 'Player Name', 'INF', 'SO', '6\'0"', '185', 'R/R'],
  ['00', 'Player Name', 'RHP', 'FR', '6\'2"', '195', 'R/R'],
  ['00', 'Player Name', 'OF', 'SO', '5\'11"', '175', 'L/L'],
  ['00', 'Player Name', 'C', 'FR', '6\'0"', '200', 'R/R'],
  ['00', 'Player Name', 'LHP', 'SO', '6\'3"', '190', 'L/L'],
  ['00', 'Player Name', '1B/3B', 'FR', '6\'1"', '205', 'R/R'],
];
const ROSTER_HEADINGS = ['No.', 'Photo', 'Name', 'Position', 'Class', 'Height', 'Weight', 'B/T'];

const SCHEDULE_PLACEHOLDERS = [
  ['Feb', '07', 'Sat', 'vs', 'Opponent College', 'Home', true, 'W', '0-0'],
  ['Feb', '08', 'Sun', '@', 'Opponent College', 'Away', false, 'L', '0-0'],
  ['Feb', '14', 'Sat', 'vs', 'Opponent College', 'Home', true, 'W', '0-0'],
  ['Feb', '21', 'Sat', '@', 'Opponent College', 'Away', true, null, '1:00 PM'],
];

const TEAM_HITTING = [
  ['AVG', '.000'], ['OBP', '.000'], ['SLG', '.000'], ['OPS', '.000'], ['R', '000'],
  ['H', '000'], ['HR', '00'], ['RBI', '000'], ['BB', '000'], ['SB', '00'],
];
const TEAM_PITCHING = [
  ['ERA', '0.00'], ['WHIP', '0.00'], ['IP', '000.0'], ['W-L', '00-00'], ['SV', '0'],
  ['K', '000'], ['BB', '000'], ['H', '000'], ['HR', '00'], ['K/9', '0.0'],
];
const TEAM_LEADERS = ['Batting Average', 'Home Runs', 'RBI', 'Stolen Bases', 'ERA', 'Strikeouts', 'Wins', 'Saves'];

const PREVIEW_TABS = [
  { value: 'roster', label: 'Roster' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'statistics', label: 'Statistics' },
];

const TILE = 'rounded-xl p-3 bg-jp-section border border-jp-outline/50';

const BlurredStatGrid = ({ metrics }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
    {metrics.map(([label, placeholder]) => (
      <div key={label} className={`${TILE} text-center`}>
        <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1">{label}</p>
        <p className="text-jp-accent-ink text-lg font-extrabold tabular-nums"><Blurred>{placeholder}</Blurred></p>
      </div>
    ))}
  </div>
);

const RosterPreview = () => {
  const cell = 'px-3 sm:px-4 py-3 sm:py-4 text-xs sm:text-sm whitespace-nowrap';
  return (
    <SectionCard title="Roster">
      <div className="overflow-x-auto rounded-lg border border-jp-line bg-jp-card">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-jp-section">
            <tr className="text-jp-ink">
              {ROSTER_HEADINGS.map((heading) => (
                <th key={heading} scope="col" className={`${cell} font-semibold text-center uppercase`}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROSTER_PLACEHOLDERS.map(([number, name, position, playerClass, height, weight, batThrow], index) => (
              <tr key={index} className="border-b border-jp-line last:border-b-0">
                <td className={`${cell} text-jp-muted text-center`}><Blurred>{number}</Blurred></td>
                <td className={cell}>
                  <span className="block w-9 h-9 sm:w-10 sm:h-10 mx-auto rounded-full border border-jp-line bg-gradient-to-br from-jp-navy to-cyan-900 blur-[3px]" aria-hidden="true"></span>
                </td>
                <td className={`${cell} text-jp-ink font-semibold`}><Blurred>{name}</Blurred></td>
                {[position, playerClass, height, weight, batThrow].map((value, i) => (
                  <td key={i} className={`${cell} text-jp-muted text-center`}><Blurred>{value}</Blurred></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
};

const RESULT_STYLES = {
  W: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  L: 'bg-red-500/15 text-red-600 dark:text-red-400',
};

const SchedulePreview = () => (
  <div className="space-y-6">
    <SectionCard title="Record">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {['Overall', 'Conference', 'Home', 'Away'].map((label) => (
          <div key={label} className={`${TILE} text-center`}>
            <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1">{label}</p>
            <p className="text-jp-accent-ink text-lg font-extrabold tabular-nums"><Blurred>00-00</Blurred></p>
          </div>
        ))}
      </div>
    </SectionCard>

    <SectionCard title="Games">
      <ul className="space-y-2">
        {SCHEDULE_PLACEHOLDERS.map(([month, day, weekday, where, opponent, site, conference, result, score], index) => (
          <li key={index} className={`flex items-center gap-4 px-4 py-3 ${TILE}`}>
            <div className="w-12 shrink-0 text-center">
              <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest"><Blurred>{month}</Blurred></p>
              <p className="text-jp-ink text-xl font-extrabold leading-none tabular-nums"><Blurred>{day}</Blurred></p>
              <p className="text-jp-muted text-[10px] font-semibold uppercase"><Blurred>{weekday}</Blurred></p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-jp-ink text-sm font-semibold truncate">
                <span className="text-jp-muted font-bold mr-1">{where}</span>
                <Blurred>{opponent}</Blurred>
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-jp-muted">
                <span>{site}</span>
                {conference && <span className="px-1.5 py-0.5 rounded bg-jp-accent/15 text-jp-accent-ink">Conference</span>}
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-2">
              {result && (
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${RESULT_STYLES[result]}`}>
                  <Blurred>{result}</Blurred>
                </span>
              )}
              <span className={result ? 'text-jp-ink font-extrabold tabular-nums' : 'text-jp-muted text-sm font-semibold tabular-nums'}>
                <Blurred>{score}</Blurred>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  </div>
);

const StatisticsPreview = () => (
  <div className="space-y-6">
    <SectionCard title="Team Hitting">
      <BlurredStatGrid metrics={TEAM_HITTING} />
    </SectionCard>
    <SectionCard title="Team Pitching">
      <BlurredStatGrid metrics={TEAM_PITCHING} />
    </SectionCard>
    <SectionCard title="Team Leaders">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TEAM_LEADERS.map((label) => (
          <div key={label} className={`flex items-center justify-between gap-3 px-4 py-3 ${TILE}`}>
            <span className="min-w-0">
              <span className="block text-jp-muted text-[10px] font-bold uppercase tracking-widest">{label}</span>
              <span className="block text-jp-ink text-sm font-semibold truncate"><Blurred>Player Name</Blurred></span>
            </span>
            <span className="text-jp-accent-ink text-lg font-extrabold tabular-nums"><Blurred>00</Blurred></span>
          </div>
        ))}
      </div>
    </SectionCard>
  </div>
);

const COACH_ICON = 'w-4 h-4 flex-shrink-0';

// Head coach row at the bottom of the navy header, as on the coach team page
const CoachContactPreview = () => (
  <div className="mt-6 pt-5 border-t border-white/15 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm">
    <div className="flex items-center gap-2 text-white">
      <svg className={`${COACH_ICON} text-yellow-300`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
      <span className="font-semibold"><Blurred>Head Coach Name</Blurred></span>
    </div>
    <div className="flex items-center gap-2 text-slate-300">
      <svg className={COACH_ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
      <Blurred>(555) 555-0123</Blurred>
    </div>
    <div className="flex items-center gap-2 text-slate-300">
      <svg className={COACH_ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
      <Blurred>coach@college.edu</Blurred>
    </div>
  </div>
);

export const TeamPreviewPage = () => {
  const { state } = useLocation();
  const [activeTab, setActiveTab] = useState('roster');
  const team = state?.team;

  if (!team) return <MembersOnly kind="team" />;

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      <PreviewHeader
        eyebrow="Team Profile"
        title={team.name}
        redirectTo={{ kind: 'team', id: team.id }}
        logo={<TeamLogo team={team.name} logo={team.logo} size="lg" />}
        subtitle={
          <span>
            {state.division && <>{state.division} · </>}
            <Blurred>Conference · City, ST</Blurred>
          </span>
        }
        aside={
          // Following a team is for members, so this goes to sign up
          <Link
            to="/auth/signup"
            className="self-start sm:self-auto flex-shrink-0 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            <span>Follow Team</span>
          </Link>
        }
        footer={<CoachContactPreview />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 space-y-6">
        <SectionCard title="Team Information">
          <InfoGrid
            wide
            items={[
              // Known only when the leaderboard was filtered to one division
              { label: 'Division', value: state.division || 'NJCAA D1', visible: Boolean(state.division) },
              { label: 'Region', value: 'Region 00' },
              { label: 'Conference', value: 'Conference name' },
              { label: 'Location', value: 'City, ST' },
              { label: 'Rank', value: '00' },
              { label: 'Overall Record', value: '00-00' },
              { label: 'Streak', value: 'W0' },
              { label: 'Home', value: '00-00' },
              { label: 'Away', value: '00-00' },
              { label: 'Neutral', value: '0-0' },
              { label: 'Conference Record', value: '00-00' },
            ]}
          />
        </SectionCard>

        <div className="flex justify-center sm:justify-start">
          <SegmentedToggle options={PREVIEW_TABS} value={activeTab} onChange={setActiveTab} ariaLabel="Team sections" />
        </div>

        {activeTab === 'roster' && <RosterPreview />}
        {activeTab === 'schedule' && <SchedulePreview />}
        {activeTab === 'statistics' && <StatisticsPreview />}
      </div>
    </main>
  );
};
